import React from 'react'

import type { EventInput } from '@fullcalendar/core'
import type { Task, WorkdayConfig } from '@ztechapps/common.contract'

import isEmpty from 'lodash/isEmpty'

import { TaskStatus } from '@ztechapps/common.contract'
import { useFormatWorkdayTime, useUser } from '@ztechapps/common.hook'
import { findStatus, resolveCalendarEventColor } from '@ztechapps/webui.util'
import { buildTaskTitle, computeTaskEndDate } from '@ztechapps/task.common'

const buildTooltip = (item: Task, formatTime: (m: number) => string): string => {
  const parts: string[] = []
  if (item?.workOrder?.code) parts.push(`Production: ${item.workOrder.code}`)
  if (item?.project?.code) parts.push(`Project: ${item.project.code}`)
  if (item?.timeSpent) parts.push(`Time Spent: ${formatTime(item.timeSpent)}`)
  return parts.join('\n')
}

type MapEventOptions = {
  formatTime: (m: number) => string
  workdayConfig: WorkdayConfig
  timeZone?: string
}

const mapTaskToEvent = (item: Task, options: MapEventOptions): EventInput => {
  const { formatTime, workdayConfig, timeZone } = options
  const eventColor = resolveCalendarEventColor(findStatus(TaskStatus, item?.status))
  const title = buildTaskTitle(item)

  return {
    id: item?._id,
    title,
    start: item?.startDate,
    end: computeTaskEndDate(item?.startDate, item?.timeSpent, workdayConfig, timeZone),
    ...eventColor,
    extendedProps: {
      type: 'Project',
      icon: '',
      renderEventContent: (
        <div className="p-1" style={{ backgroundColor: eventColor.backgroundColor, color: eventColor.textColor }}>
          {title}
        </div>
      ),
      tooltip: buildTooltip(item, formatTime),
      ...item,
    },
  }
}

export type UseGetEvents = { eventsLocal: EventInput[] }
export const useGetEvents = (list: Task[]): UseGetEvents => {
  const [eventsLocal, setEventsLocal] = React.useState<EventInput[]>([])
  const { formatTime, workdayConfig } = useFormatWorkdayTime()
  const { user } = useUser()

  React.useEffect(() => {
    const items = list ?? []
    const mappedEvents: EventInput[] = []
    const mapOptions: MapEventOptions = { formatTime, workdayConfig, timeZone: user?.timeZone }

    // Collect IDs of subtasks that will be created from their parent's subTasks array
    const subtaskIdsFromParents = new Set<string>()
    for (const item of items) {
      if (!isEmpty(item?.subTasks)) {
        for (const subTask of item.subTasks) {
          subtaskIdsFromParents.add(subTask?._id)
        }
      }
    }

    for (const item of items) {
      // Skip standalone entry if this task will appear as a subtask of its parent
      if (!subtaskIdsFromParents.has(item?._id)) {
        mappedEvents.push(mapTaskToEvent(item, mapOptions))
      }

      // Always expand subtasks regardless of whether the parent is skipped
      if (!isEmpty(item?.subTasks)) {
        for (const subTask of item.subTasks) {
          const subTaskWithDefaults = {
            ...subTask,
            startDate: subTask?.startDate ?? item?.startDate,
            endDate: subTask?.endDate ?? item?.endDate,
            timeSpent: subTask?.timeSpent ?? item?.timeSpent,
            status: subTask?.status ?? item?.status,
            project: item?.project,
            workOrder: item?.workOrder,
          }
          mappedEvents.push(mapTaskToEvent(subTaskWithDefaults, mapOptions))
        }
      }
    }

    setEventsLocal(mappedEvents)
  }, [list, formatTime, workdayConfig, user?.timeZone])

  return { eventsLocal }
}
